var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable, action } from "mobx";
export class GlobalAdminDocumentRetrievalViewModel {
    constructor(container) {
        this.patientIdInput = "";
        this.firstNameInput = "";
        this.lastNameInput = "";
        this.emailInput = "";
        this.errorMessage = null;
        this.successMessage = null;
        this.kbDetailsExpanded = false;
        this.s3DetailsExpanded = false;
        this.batchesExpanded = true;
        this.globalAdminRetrievalManager = container.globalAdminRetrievalManager;
        this.bannerUIManager = container.bannerUIManager;
    }
    get summary() {
        return this.globalAdminRetrievalManager.summary.value;
    }
    get patientDetails() {
        return this.globalAdminRetrievalManager.currentPatientDetails;
    }
    get isLoading() {
        return this.globalAdminRetrievalManager.loadingPatientDetails;
    }
    get isActionLoading() {
        return this.globalAdminRetrievalManager.actionLoading;
    }
    get canMigrate() {
        if (!this.patientDetails)
            return false;
        return this.patientDetails.status.needsMigration;
    }
    get searchResults() {
        return this.globalAdminRetrievalManager.searchResults;
    }
    get isSearchLoading() {
        return this.globalAdminRetrievalManager.searchLoading;
    }
    get hasSearchQuery() {
        return !!(this.firstNameInput.trim() || this.lastNameInput.trim() || this.emailInput.trim());
    }
    setPatientIdInput(value) {
        this.patientIdInput = value;
        this.clearMessages();
    }
    setFirstNameInput(value) {
        this.firstNameInput = value;
    }
    setLastNameInput(value) {
        this.lastNameInput = value;
    }
    setEmailInput(value) {
        this.emailInput = value;
    }
    clearMessages() {
        this.errorMessage = null;
        this.successMessage = null;
    }
    clearSearchInputs() {
        this.firstNameInput = "";
        this.lastNameInput = "";
        this.emailInput = "";
        this.globalAdminRetrievalManager.clearSearchResults();
    }
    toggleKbDetails() {
        this.kbDetailsExpanded = !this.kbDetailsExpanded;
    }
    toggleS3Details() {
        this.s3DetailsExpanded = !this.s3DetailsExpanded;
    }
    toggleBatches() {
        this.batchesExpanded = !this.batchesExpanded;
    }
    async lookupPatient() {
        const patientId = this.patientIdInput.trim();
        if (!patientId) {
            this.errorMessage = "Please enter a patient ID";
            return;
        }
        this.clearMessages();
        const details = await this.globalAdminRetrievalManager.loadPatientDetails(patientId);
        if (!details) {
            this.errorMessage = "Patient not found or error fetching data";
        }
    }
    async searchPatients() {
        if (!this.hasSearchQuery) {
            this.errorMessage = "Please enter at least one search criteria";
            return;
        }
        this.clearMessages();
        const results = await this.globalAdminRetrievalManager.searchPatients({
            firstName: this.firstNameInput.trim() || undefined,
            lastName: this.lastNameInput.trim() || undefined,
            email: this.emailInput.trim() || undefined,
        });
        if (results.length === 0) {
            this.errorMessage = "No patients found matching the search criteria";
        }
    }
    async selectPatient(patient) {
        this.patientIdInput = patient.id;
        this.clearSearchInputs();
        this.clearMessages();
        const details = await this.globalAdminRetrievalManager.loadPatientDetails(patient.id);
        if (!details) {
            this.errorMessage = "Error loading patient details";
        }
    }
    async migratePatient() {
        if (!this.patientDetails)
            return;
        this.clearMessages();
        const result = await this.globalAdminRetrievalManager.migratePatient(this.patientDetails.status.patientId);
        if (result.success) {
            this.successMessage = result.message;
            this.bannerUIManager.displayBanner({ text: "Migration started successfully" });
        }
        else {
            this.errorMessage = result.message;
        }
    }
    refresh() {
        this.globalAdminRetrievalManager.refresh();
        if (this.patientDetails) {
            void this.globalAdminRetrievalManager.loadPatientDetails(this.patientDetails.status.patientId);
        }
    }
    clearCurrentPatient() {
        this.globalAdminRetrievalManager.clearCurrentPatient();
        this.patientIdInput = "";
        this.clearMessages();
    }
    getMigrationStatusVariant(status) {
        switch (status) {
            case "COMPLETE":
                return "success";
            case "PENDING":
                return "warning";
            case "IN_PROGRESS":
                return "info";
            case "NOT_NEEDED":
                return "secondary";
            default:
                return "secondary";
        }
    }
    getVectorizationStatusVariant(status) {
        if (!status)
            return "secondary";
        switch (status.toUpperCase()) {
            case "COMPLETE":
            case "COMPLETED":
            case "SUCCESS":
                return "success";
            case "IN_PROGRESS":
            case "PENDING":
                return "warning";
            case "FAILED":
            case "ERROR":
                return "error";
            default:
                return "secondary";
        }
    }
    formatDate(dateStr) {
        if (!dateStr)
            return "-";
        const date = new Date(dateStr);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    }
    formatDuration(startStr, endStr) {
        if (!startStr)
            return "-";
        if (!endStr) {
            const start = new Date(startStr);
            const now = new Date();
            const diffMs = now.getTime() - start.getTime();
            return this.formatDurationMs(diffMs) + " (in progress)";
        }
        const start = new Date(startStr);
        const end = new Date(endStr);
        const diffMs = end.getTime() - start.getTime();
        return this.formatDurationMs(diffMs);
    }
    formatDurationMs(ms) {
        if (ms < 0)
            return "0:00:00";
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const pad = (n) => n.toString().padStart(2, "0");
        return `${hours}:${pad(minutes)}:${pad(seconds)}`;
    }
}
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "patientIdInput", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "firstNameInput", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "lastNameInput", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "emailInput", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "errorMessage", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "successMessage", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "kbDetailsExpanded", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "s3DetailsExpanded", void 0);
__decorate([
    observable
], GlobalAdminDocumentRetrievalViewModel.prototype, "batchesExpanded", void 0);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "summary", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "patientDetails", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "isLoading", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "isActionLoading", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "canMigrate", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "searchResults", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "isSearchLoading", null);
__decorate([
    computed
], GlobalAdminDocumentRetrievalViewModel.prototype, "hasSearchQuery", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "setPatientIdInput", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "setFirstNameInput", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "setLastNameInput", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "setEmailInput", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "clearMessages", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "clearSearchInputs", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "toggleKbDetails", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "toggleS3Details", null);
__decorate([
    action
], GlobalAdminDocumentRetrievalViewModel.prototype, "toggleBatches", null);
